/**
 * CSV string generation utility for DataList export.
 *
 * Pure utility functions with no React dependency.
 * Handles CSV escaping, string building, and browser file download.
 */

import { safeString } from './cellRenderer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Column descriptor used for CSV export.
 */
export interface CsvColumn {
  key: string;
  label: string;
}

/**
 * Options for CSV generation.
 */
export interface CsvOptions {
  /** Whether to include column headers. Default: true */
  includeHeaders?: boolean;
  /** Column separator. Default: ',' */
  separator?: string;
}

// ---------------------------------------------------------------------------
// escapeCsvValue
// ---------------------------------------------------------------------------

/**
 * Escapes a value for CSV format.
 * Wraps in quotes if the value contains commas, quotes, or newlines.
 * Doubles any existing quotes.
 */
export function escapeCsvValue(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

// ---------------------------------------------------------------------------
// buildCsvString
// ---------------------------------------------------------------------------

/**
 * Builds a CSV string from columns and rows.
 *
 * @param columns - Column definitions with key and label
 * @param rows - Array of row data
 * @param getCellValue - Function to extract cell value from a row
 * @param options - CSV options
 * @returns Complete CSV string
 */
export function buildCsvString(
  columns: CsvColumn[],
  rows: Record<string, unknown>[],
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
  options?: CsvOptions,
): string {
  const includeHeaders = options?.includeHeaders ?? true;
  const separator = options?.separator ?? ',';

  const lines: string[] = [];

  if (includeHeaders) {
    const headerLine = columns
      .map((col) => escapeCsvValue(col.label))
      .join(separator);
    lines.push(headerLine);
  }

  for (const row of rows) {
    const rowLine = columns
      .map((col) => {
        const value = getCellValue(row, col.key);
        const stringValue = safeString(value);
        return escapeCsvValue(stringValue);
      })
      .join(separator);
    lines.push(rowLine);
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// downloadCsv
// ---------------------------------------------------------------------------

/**
 * Triggers a file download in the browser.
 * Creates a Blob with text/csv type and triggers download via a temporary anchor.
 * Prepends BOM for Excel compatibility.
 */
export function downloadCsv(csvString: string, fileName: string): void {
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvString], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
